export const isMobileOrTVDevice = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = (navigator.userAgent || '').toLowerCase();
  const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i.test(ua);
  const isTVUA = /smart-tv|smarttv|googletv|appletv|hbbtv|pov_tv|netcast.tv|viera|tizen|webos|bravia/i.test(ua);
  const isTouchOrSmallScreen = window.innerWidth <= 1024 || ('ontouchstart' in window && navigator.maxTouchPoints > 0);
  return isMobileUA || isTVUA || isTouchOrSmallScreen;
};

export const isAppFullscreen = (): boolean => {
  if (typeof document === 'undefined') return false;
  const doc = document as any;
  return !!(
    doc.fullscreenElement ||
    doc.webkitFullscreenElement ||
    doc.mozFullScreenElement ||
    doc.msFullscreenElement
  );
};

export const requestAppFullscreen = async (): Promise<boolean> => {
  if (typeof document === 'undefined') return false;
  if (isAppFullscreen()) return true;
  try {
    const elem = document.documentElement as any;
    if (elem.requestFullscreen) {
      await elem.requestFullscreen();
      return true;
    } else if (elem.webkitRequestFullscreen) {
      await elem.webkitRequestFullscreen();
      return true;
    }
  } catch {}
  return false;
};

export const isVideoInFullscreen = (video?: HTMLVideoElement | null): boolean => {
  if (isAppFullscreen()) return true;
  if (video && (video as any).webkitDisplayingFullscreen) return true;
  return false;
};

export const toggleVideoFullscreen = async (
  container?: HTMLElement | null,
  video?: HTMLVideoElement | null
): Promise<boolean> => {
  if (typeof document === 'undefined') return false;
  const doc = document as any;

  // 1. If currently in fullscreen -> Exit fullscreen
  if (isAppFullscreen() || (video && (video as any).webkitDisplayingFullscreen)) {
    try {
      if (doc.exitFullscreen) {
        await doc.exitFullscreen();
      } else if (doc.webkitExitFullscreen) {
        await doc.webkitExitFullscreen();
      } else if (doc.mozCancelFullScreen) {
        await doc.mozCancelFullScreen();
      } else if (doc.msExitFullscreen) {
        await doc.msExitFullscreen();
      } else if (video && (video as any).webkitExitFullscreen) {
        (video as any).webkitExitFullscreen();
      }
      // Unlock orientation so sensors stay 100% free
      if (typeof screen !== 'undefined' && (screen as any).orientation?.unlock) {
        (screen as any).orientation.unlock();
      }
      return false;
    } catch (err) {
      console.warn('Erro ao sair de tela cheia:', err);
      return false;
    }
  }

  // 2. Request Fullscreen (Native Video Player on iOS & Android)
  try {
    // iOS Safari native fullscreen on video
    if (video && typeof (video as any).webkitEnterFullscreen === 'function') {
      (video as any).webkitEnterFullscreen();
      return true;
    }

    // Android / Chrome: Request Fullscreen directly on <video> element for native Android player
    const target = video || container || document.documentElement;
    const elem = target as any;

    if (elem.requestFullscreen) {
      await elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      await elem.webkitRequestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      await elem.mozRequestFullScreen();
    } else if (elem.msRequestFullscreen) {
      await elem.msRequestFullscreen();
    }

    // Unlock / allow rotation
    if (typeof screen !== 'undefined' && (screen as any).orientation?.unlock) {
      (screen as any).orientation.unlock();
    }
    return true;
  } catch (err) {
    console.warn('Erro ao solicitar tela cheia:', err);
    // Fallback: request on container/root element
    try {
      const fallbackTarget = (container || document.documentElement) as any;
      if (fallbackTarget.requestFullscreen) {
        await fallbackTarget.requestFullscreen();
        return true;
      }
    } catch {}
    return false;
  }
};
